import type { ReactNode } from 'react'

import { cn } from '../utilitarios/cn'

/** Só `http` e `https`. Ver a nota de `ehUrlExterna`. */
const PROTOCOLOS_ACEITOS = /^https?:\/\/\S/i

/**
 * A URL é segura para virar `href`?
 *
 * **Allowlist de protocolo, não denylist.** `javascript:`, `data:` e `vbscript:` em
 * `href` executam script no clique, e um `href` deste sistema pode vir de payload de
 * integração — texto de terceiro. Barrar os três conhecidos deixaria passar o quarto;
 * exigir `http`/`https` não deixa passar nenhum.
 *
 * Exige ao menos um caractere não-branco depois do `//`: `https://` sozinho não é
 * endereço, e viraria um link que não vai a lugar nenhum.
 */
export function ehUrlExterna(valor: string): boolean {
  return PROTOCOLOS_ACEITOS.test(valor.trim())
}

export interface PropriedadesLinkExterno {
  /** A URL, **exatamente** como recebida — query string inclusive. */
  href: string
  /** O texto visível. É ele que evita a URL crua na tela. */
  children: ReactNode
  className?: string
}

/**
 * Link para fora do sistema — **a única definição de âncora externa do projeto.**
 *
 * ## Por que existe
 *
 * A timeline do lead exibe endereços que chegam de integração: o imóvel de interesse
 * (`link`), o histórico da conversa na Lais (`conversation_history`), a página do
 * anúncio. Duas coisas precisavam ser verdade ao mesmo tempo:
 *
 *  1. **A URL não aparece crua.** `https://lais.ai/history?chatid=1` no meio de uma
 *     frase é ilegível e quebra o layout em telas estreitas; o que o corretor precisa é
 *     de algo para clicar.
 *  2. **`rel="noreferrer noopener"` em toda âncora com `target="_blank"`.** Sem
 *     `noopener`, a página aberta alcança esta por `window.opener` e pode navegá-la —
 *     e o destino aqui é sempre de terceiro. `noreferrer` completa, não vazando a URL
 *     interna do CRM no `Referer`.
 *
 * O componente existe para que o item 2 não dependa de ninguém lembrar. Antes, a
 * âncora era escrita à mão em `ValorDoPayload`; qualquer segundo lugar que precisasse
 * de link repetiria os atributos — e é o tipo de repetição em que a terceira cópia
 * esquece o `rel`.
 *
 * ## O rótulo é do chamador
 *
 * `children` e não um texto fixo, porque o contexto decide: no corpo da interação a
 * frase já diz o que é ("Link do imóvel: **Clique Aqui**"), e no painel de auditoria a
 * chave técnica está ao lado ("`conversation_history`: **abrir link**"). Um rótulo
 * único obrigaria um dos dois a ficar redundante.
 */
export function LinkExterno({ href, children, className }: PropriedadesLinkExterno) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      // A URL completa no `title`: quem quiser conferir o destino antes de clicar
      // passa o mouse, sem que ela ocupe a linha. O navegador já mostra o destino na
      // barra de status no hover, mas o `title` funciona também em toque.
      title={href}
      className={cn('text-marca-600 hover:underline', className)}
    >
      {children}
    </a>
  )
}
